import { GppMaybe, InfoOutlined } from '@mui/icons-material';
import { Alert, AlertTitle } from '@mui/material';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { MONITORING_FLAGS, SECURITY_MONITORING_FLAGS } from '../../consts';
import messages from '../../intl/messages';
import { getCurrentExam } from '../../store/model/selectors';
import formatDate from '../../utils/formatDate';

const useStyles = makeStyles()(() => ({
	bounding: {
		marginBottom: '15px',
		'& .MuiAlert-root': {
			marginBottom: '10px',
		},
		'& .flags': {
			maxHeight: '150px',
			overflowY: 'auto',
			fontSize: '0.9rem',
		},
		'& .flag': {
			margin: '0 0 6px 0',
		},
		'& .detail': {
			opacity: 0.7,
			overflowWrap: 'break-word',
		},
		'& .count': {
			fontWeight: 400,
			opacity: 0.7,
		},
		'& .securityNotice': {
			margin: '0 0 10px 0',
			fontSize: '0.8rem',
			opacity: 0.7,
		},
	},
}));

const FLAG_MESSAGES = {
	[MONITORING_FLAGS.REPLAY_SUSPECTED]: messages.monitoringFlagReplaySuspected,
	[MONITORING_FLAGS.MONITORING_CONTENDED]: messages.monitoringFlagMonitoringContended,
	[MONITORING_FLAGS.MONITORING_RESET]: messages.monitoringFlagMonitoringReset,
	[MONITORING_FLAGS.CLOCK_DRIFT]: messages.monitoringFlagClockDrift,
	[MONITORING_FLAGS.PERMISSION_DENIED]: messages.monitoringFlagPermissionDenied,
	[MONITORING_FLAGS.PROCESS_LIST_ERROR]: messages.monitoringFlagProcessListError,
	[MONITORING_FLAGS.CHALLENGE_DESYNC]: messages.monitoringFlagChallengeDesync,
	[MONITORING_FLAGS.VIDEO_RECHECK_BUDGET_SPENT]: messages.monitoringFlagVideoRecheckBudgetSpent,
	[MONITORING_FLAGS.MONITORING_DEGRADED]: messages.monitoringFlagMonitoringDegraded,
};

const Flag = ({ error, detail, timestamp, count, lastTimestamp }) => {
	// records written before task 15278 have no count field - treat them as a single occurrence
	const occurrences = count ?? 1;
	// count > 1 without lastTimestamp shouldn't happen, but fall back to a single timestamp instead of breaking the render
	const hasRange = occurrences > 1 && !!lastTimestamp;

	return (
		<p className="flag">
			<strong>
				{hasRange ? (
					<FormattedMessage
						{...messages.monitoringFlagTimeRange}
						values={{ from: formatDate(timestamp), to: formatDate(lastTimestamp) }}
					/>
				) : (
					formatDate(timestamp)
				)}
			</strong>
			{occurrences > 1 && (
				<span className="count">
					{' '}
					<FormattedMessage {...messages.monitoringFlagCount} values={{ count: occurrences }} />
				</span>
			)}
			<br />
			<FormattedMessage {...(FLAG_MESSAGES[error] ?? messages.monitoringFlagUnknown)} />
			{detail && (
				<>
					<br />
					<span className="detail">{detail}</span>
				</>
			)}
		</p>
	);
};

Flag.propTypes = {
	error: PropTypes.string,
	detail: PropTypes.string,
	timestamp: PropTypes.string,
	count: PropTypes.number,
	lastTimestamp: PropTypes.string,
};

// Reviewer-only view - the candidate never sees exam.monitoringError
const MonitoringFlags = () => {
	const { classes } = useStyles();
	const exam = useSelector(getCurrentExam);

	const [security, operational] = useMemo(() => {
		const flags = Array.isArray(exam?.monitoringError) ? exam.monitoringError : [];

		return [
			flags.filter(({ error }) => SECURITY_MONITORING_FLAGS.includes(error)),
			flags.filter(({ error }) => !SECURITY_MONITORING_FLAGS.includes(error)),
		];
	}, [exam?.monitoringError]);

	if (!security.length && !operational.length) return null;

	return (
		<div className={classes.bounding}>
			{security.length > 0 && (
				<Alert severity="warning" icon={<GppMaybe fontSize="inherit" />}>
					<AlertTitle>
						<FormattedMessage {...messages.monitoringSecurityFlags} />
					</AlertTitle>
					<p className="securityNotice">
						<FormattedMessage {...messages.monitoringFlagSecurityNotice} />
					</p>
					<div className="flags">
						{security.map((flag, index) => (
							<Flag key={`security-${flag.timestamp}-${index}`} {...flag} />
						))}
					</div>
				</Alert>
			)}
			{operational.length > 0 && (
				<Alert severity="info" variant="outlined" icon={<InfoOutlined fontSize="inherit" />}>
					<AlertTitle>
						<FormattedMessage {...messages.monitoringOperationalFlags} />
					</AlertTitle>
					<div className="flags">
						{operational.map((flag, index) => (
							<Flag key={`operational-${flag.timestamp}-${index}`} {...flag} />
						))}
					</div>
				</Alert>
			)}
		</div>
	);
};

export default MonitoringFlags;
